import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Notification, PaginationParams } from '@/types'
import { notificationsApi } from '@/api/client'

export const useNotificationStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  })

  // Getters
  const unreadNotifications = computed(() => {
    return notifications.value.filter(n => !n.read)
  })

  const unreadCount = computed(() => {
    return unreadNotifications.value.length
  })

  const recentNotifications = computed(() => {
    return notifications.value.slice(0, 5)
  })

  // Actions
  const fetchNotifications = async (params?: PaginationParams) => {
    loading.value = true
    error.value = null
    try {
      const response = await notificationsApi.getAll(params)
      notifications.value = response.data
      pagination.value = {
        page: response.pagination.page,
        pageSize: response.pagination.page_size,
        total: response.pagination.total,
        totalPages: response.pagination.total_pages,
      }
      return response
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch notifications'
      throw err
    } finally {
      loading.value = false
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId)

      // Update locally
      const notification = notifications.value.find(n => n.id === notificationId)
      if (notification) {
        notification.read = true
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to mark notification as read'
      throw err
    }
  }

  const markAllAsRead = async () => {
    loading.value = true
    error.value = null
    try {
      await notificationsApi.markAllAsRead()

      // Update all locally
      notifications.value.forEach(n => {
        n.read = true
      })
    } catch (err: any) {
      error.value = err.message || 'Failed to mark all as read'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationsApi.delete(notificationId)
      notifications.value = notifications.value.filter(n => n.id !== notificationId)
    } catch (err: any) {
      error.value = err.message || 'Failed to delete notification'
      throw err
    }
  }

  const addNotification = (notification: Notification) => {
    // Add new notification (e.g., from WebSocket)
    notifications.value.unshift(notification)
  }

  return {
    // State
    notifications,
    loading,
    error,
    pagination,
    // Getters
    unreadNotifications,
    unreadCount,
    recentNotifications,
    // Actions
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
  }
})
